import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Palette, ExternalLink, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';

interface CanvaDesign {
  id: string;
  title: string;
  thumbnail?: {
    url: string;
  };
  urls?: {
    view_url: string;
    edit_url: string;
  };
  created_at: string;
  updated_at: string;
}

interface ConnectCanvaDesignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  onSuccess: () => void;
}

export const ConnectCanvaDesignModal: React.FC<ConnectCanvaDesignModalProps> = ({
  open,
  onOpenChange,
  collectionId,
  onSuccess,
}) => {
  const [designs, setDesigns] = useState<CanvaDesign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDesign, setSelectedDesign] = useState<CanvaDesign | null>(null);
  const { toast } = useToast();

  const fetchCanvaDesigns = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('canva-list-designs', {
        body: { search: searchQuery }
      });

      if (error) {
        console.error('Error fetching Canva designs:', error);
        toast({
          title: "Error",
          description: "Failed to fetch designs from Canva. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (data?.items) {
        setDesigns(data.items);
      } else {
        setDesigns([]);
      }
    } catch (error) {
      console.error('Exception fetching Canva designs:', error);
      toast({
        title: "Error",
        description: "An error occurred while fetching designs.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const connectDesign = async () => {
    if (!selectedDesign) return;

    setIsConnecting(true);
    try {
      // Get user session for authentication (like other components)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Authentication error:', sessionError);
        toast({
          title: "Authentication Error",
          description: "Please ensure you're logged in and try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('🔐 [ConnectDesign] Calling edge function with session check');

      // Call without explicit Authorization header - let invokeEdgeFunction handle it
      const result = await invokeEdgeFunction('canva-connect-design', {
        collectionId,
        designId: selectedDesign.id,
        designTitle: selectedDesign.title,
        designThumbnail: selectedDesign.thumbnail?.url,
        designViewUrl: selectedDesign.urls?.view_url,
        designEditUrl: selectedDesign.urls?.edit_url,
      });

      console.log('✅ [ConnectDesign] Edge function result:', result);
      
      // Check if there's an error in the result
      if (result.error) {
        console.error('❌ [ConnectDesign] Edge function error:', result.error);
        toast({
          title: "Connection Failed",
          description: result.error.message || "Failed to connect design. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Check if the operation was successful
      if (!result.data || !result.data.success) {
        console.error('❌ [ConnectDesign] Edge function failed:', result.data);
        toast({
          title: "Connection Failed", 
          description: result.data?.error || "Failed to connect design. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Design connected successfully!",
      });

      onSuccess();
      onOpenChange(false);
      setSelectedDesign(null);
    } catch (error) {
      console.error('Exception connecting design:', error);
      toast({
        title: "Error",
        description: "An error occurred while connecting the design.",
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCanvaDesigns();
    }
  }, [open]);

  const handleSearch = () => {
    fetchCanvaDesigns();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        {/* Fixed Header */}
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Connect Canva Design</DialogTitle>
          <DialogDescription>
            Select a design from your Canva account to connect to this collection.
          </DialogDescription>
        </DialogHeader>

        {/* Fixed Search Section */}
        <div className="flex-shrink-0 flex gap-2 mb-4">
          <Input
            placeholder="Search your designs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button onClick={handleSearch} disabled={isLoading}>
            <Search className="w-4 h-4" />
          </Button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                <p className="text-gray-600">Fetching your designs...</p>
              </div>
            </div>
          )}

          {/* Designs Grid */}
          {!isLoading && designs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {designs.map((design) => (
                <Card
                  key={design.id}
                  className={`cursor-pointer transition-all ${
                    selectedDesign?.id === design.id
                      ? 'ring-2 ring-purple-500 bg-purple-50'
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => setSelectedDesign(design)}
                >
                  <CardHeader className="p-3">
                    <div className="aspect-video bg-gray-100 rounded-md mb-2 flex items-center justify-center overflow-hidden">
                      {design.thumbnail?.url ? (
                        <img
                          src={design.thumbnail.url}
                          alt={design.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Palette className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <CardTitle className="text-sm font-medium line-clamp-2">
                        {design.title}
                      </CardTitle>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Updated {formatDate(design.updated_at)}</span>
                        {selectedDesign?.id === design.id && (
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                            <Check className="w-3 h-3 mr-1" />
                            Selected
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}

          {/* No Results */}
          {!isLoading && designs.length === 0 && (
            <div className="text-center py-8">
              <Palette className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No designs found</h3>
              <p className="text-gray-600">
                {searchQuery
                  ? `No designs match "${searchQuery}". Try a different search term.`
                  : 'You don\'t have any designs yet. Create some designs in Canva first.'}
              </p>
            </div>
          )}

          {/* Selected Design Details */}
          {selectedDesign && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="text-sm">Selected Design</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                    {selectedDesign.thumbnail?.url ? (
                      <img
                        src={selectedDesign.thumbnail.url}
                        alt={selectedDesign.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Palette className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium">{selectedDesign.title}</h4>
                    <p className="text-sm text-gray-600">
                      Created {formatDate(selectedDesign.created_at)}
                    </p>
                  </div>
                  {selectedDesign.urls?.view_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(selectedDesign.urls!.view_url, '_blank');
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="flex-shrink-0 flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={connectDesign}
            disabled={!selectedDesign || isConnecting}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Design'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};